import { useState, useEffect } from 'react';

/**
 * A persisted record: one localStorage or sessionStorage key, owned by one domain. Raw storage
 * access belongs here only. Keep the wire format byte-identical. See
 * .claude/context/session-identity.md
 * Every loader version reads these records: see .claude/context/persisted-values.md.
 */

// Matches thd-storage-utils' encodeStorage: encodeURIComponent leaves `!'()*` alone.
const encode = (raw) => encodeURIComponent(raw).replace(/\(/g, '%28').replace(/\)/g, '%29');

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// `session` keeps a record for this tab only; every other record is `local`.
const storageFor = (storage) => (storage === 'session' ? window.sessionStorage : window.localStorage);

const hasStorage = (storage) => typeof window !== 'undefined' && !!storageFor(storage);

/**
 * Read one key's value, or null when it is absent, unparseable, or past its expiry.
 * An expired envelope is removed, the way thd-storage-utils' reader does.
 */
export const readRecord = (key, storage = 'local') => {
  if (!hasStorage(storage)) {
    return null;
  }
  let envelope;
  try {
    const raw = storageFor(storage).getItem(key);
    // decodeURIComponent is a no-op on a plain JSON string, so an unencoded envelope (what
    // tests and older writers produce) reads back fine.
    envelope = raw === null ? null : JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (envelope === null || typeof envelope !== 'object') {
    return null;
  }
  // No expiry means it never ages out; `new Date(undefined)` is an Invalid Date and every
  // comparison against it is false, which is the behaviour records written so far rely on.
  if (new Date(envelope.expiry) <= Date.now()) {
    try {
      storageFor(storage).removeItem(key);
    } catch { /* storage unavailable — nothing to remove */ }
    return null;
  }
  return envelope.value ?? null;
};

/**
 * Write one key's value in the shared envelope format. Never throws.
 * @returns {boolean} whether the storage took the write.
 */
export const writeRecord = (key, value, storage = 'local') => {
  if (!hasStorage(storage)) {
    return false;
  }
  try {
    storageFor(storage).setItem(key, encode(JSON.stringify({
      value,
      expiry: new Date(Date.now() + ONE_YEAR_MS),
    })));
    return true;
  } catch {
    // Quota or private mode. `createRecord` keeps the value in memory for this document.
    return false;
  }
};

// Records that asked for peer-tab notification, by key. One `storage` listener serves them
// all; the browser only fires it in OTHER documents, so a write here never re-enters.
const crossTabRecords = new Map();
let storageListener = null;

const ensureStorageListener = () => {
  if (storageListener || typeof window === 'undefined' || !window.addEventListener) {
    return;
  }
  storageListener = ({ key, storageArea }) => {
    if (storageArea && storageArea !== window.localStorage) {
      return;
    }
    // A null key means the peer cleared storage wholesale, which changes every record.
    crossTabRecords.forEach((record, recordKey) => {
      if (key === null || key === recordKey) {
        record.notify();
      }
    });
  };
  window.addEventListener('storage', storageListener);
};

/**
 * @param {Object} options
 * @param {string} options.key - the storage key this record owns.
 * @param {() => Object} options.buildDefault - value to use when the key is absent. Resolved
 *   at most ONCE per document and reused, so every holder here agrees on it even when the
 *   record was never persisted (two holders each minting their own would be the bug).
 * @param {boolean} [options.crossTab=false] - notify subscribers when a PEER tab writes.
 * @param {'local' | 'session'} [options.storage='local'] - where the record persists.
 * @returns {{ key: string, get: Function, set: Function, update: Function,
 *   subscribe: Function, notify: Function, seed: Function }}
 */
export const createRecord = ({
  key, buildDefault, crossTab = false, storage = 'local',
}) => {
  const listeners = new Set();
  let resolvedDefault = null;
  // The last value set here that the storage rejected. A write that succeeds drops it. See
  // .claude/context/session-identity.md ("A rejected write stays in memory").
  let unsavedValue = null;

  const notify = () => listeners.forEach((listener) => listener());

  const getDefault = () => {
    if (!resolvedDefault) {
      resolvedDefault = buildDefault();
    }
    return resolvedDefault;
  };

  /** The record's current value: a rejected write, else what is persisted, else the default. */
  const get = () => unsavedValue ?? readRecord(key, storage) ?? getDefault();

  const set = (next) => {
    unsavedValue = writeRecord(key, next, storage) ? null : next;
    notify();
    return next;
  };

  /**
   * Merge onto the CURRENT persisted value, so a writer cannot revert a field it did not touch,
   * even when the other write came from a timer that holds an old snapshot.
   * @param {Object|Function} patch - fields to merge, or `(current) => fields`.
   */
  const update = (patch) => {
    const current = get();
    return set({ ...current, ...(typeof patch === 'function' ? patch(current) : patch) });
  };

  /**
   * Persist the default if the key is absent, so peers read this value instead of minting. A
   * session record has no peer tabs, so it stays unwritten until a writer sets a value.
   */
  const seed = () => {
    const current = unsavedValue ?? readRecord(key, storage);
    if (current) {
      return current;
    }
    if (storage === 'session') {
      return getDefault();
    }
    return set(getDefault());
  };

  /**
   * Forget this record: drop the stored key, the memoized default and any rejected write. If a
   * subscriber is live, it throws before it changes anything. See
   * .claude/context/session-identity.md
   */
  const clear = () => {
    if (listeners.size > 0) {
      throw new Error(
        `Cannot clear the "${key}" record while ${listeners.size} subscriber(s) are live: `
        + 'the clear notifies, the subscriber re-reads, and a fresh default gets cached from '
        + 'the storage just emptied. Unmount (React Testing Library `cleanup()`) or '
        + 'unsubscribe first, then clear.'
      );
    }
    if (hasStorage(storage)) {
      try {
        storageFor(storage).removeItem(key);
      } catch { /* storage unavailable — nothing to remove */ }
    }
    resolvedDefault = null;
    unsavedValue = null;
    notify();
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const record = {
    key, get, set, update, seed, clear, subscribe, notify,
  };
  if (crossTab) {
    crossTabRecords.set(key, record);
    ensureStorageListener();
  }
  return record;
};

/**
 * Subscribe a component to a record. Returns `[value, update]`; `update` merges the fields.
 * At write time, read `record.get()`, not `value`. See .claude/context/session-identity.md
 */
export const useRecord = (record) => {
  const [value, setValue] = useState(record.get);

  useEffect(() => {
    record.seed();
    const unsubscribe = record.subscribe(() => setValue(record.get()));
    // A peer write (or a sibling's) can land between the first render and this effect.
    setValue(record.get());
    return unsubscribe;
  }, [record]);

  return [value, record.update];
};
